import { httpClient } from '../../http-client';
import type { AppMarketRpcClient } from '../../types/app-market-rpc-client';
import { config } from '../../config';
import { composer } from '../../component-decorators/composer';
import type { TFunction } from 'i18next';
import type {
  GetCategoriesResponse,
  GetWebSolutionsByTagsResponse,
  Order,
  WebSolutionBase,
} from '@wix/ambassador-marketplace/types';

export type Category = {
  slug: string;
  name: string;
  description: string;
  imageUrl: string;
  subCategories: SubCategory[];
  apps: (WebSolutionBase & { appIndex: string })[];
};

export type SubCategory = {
  slug: string;
  name: string;
  apps: (WebSolutionBase & { appIndex: string })[];
};

export async function getWebSolutionsByTags(
  country: string,
  lang: string,
  limit: number,
  order: Order,
  tagsSlug: string,
  rpcClient?: AppMarketRpcClient,
): Promise<GetWebSolutionsByTagsResponse> {
  const shouldUseRpcClient =
    typeof window === 'undefined' && rpcClient !== undefined;

  const getWebSolutionsByTagsResponse = shouldUseRpcClient
    ? await rpcClient.getWebSolutionsByTags({
        country,
        lang,
        limit,
        order,
        tagsSlug,
      })
    : await httpClient
        .get<GetWebSolutionsByTagsResponse>(
          `${config.baseApiUrl}/_api/marketplace-api/v1/web-solutions/tags/${tagsSlug}?limit=${limit}&lang=${lang}&order=${order}&country=${country}`,
        )
        .then(({ data }) => data);

  return getWebSolutionsByTagsResponse;
}

export async function getCategories(
  lang: string,
  rpcClient?: AppMarketRpcClient,
): Promise<GetCategoriesResponse> {
  const shouldUseRpcClient =
    typeof window === 'undefined' && rpcClient !== undefined;

  const getCategoriesResponse = shouldUseRpcClient
    ? await rpcClient.getCategories({ lang })
    : await httpClient
        .get<GetCategoriesResponse>(
          `${config.baseApiUrl}/_api/marketplace-api/v1/categories?lang=${lang}`,
        )
        .then(({ data }) => data);

  return getCategoriesResponse;
}

export function isValidCategory(
  maybeCategory: unknown,
): maybeCategory is Category {
  return (
    typeof (maybeCategory as Category).slug === 'string' &&
    typeof (maybeCategory as Category).name === 'string' &&
    typeof (maybeCategory as Category).description === 'string' &&
    typeof (maybeCategory as Category).imageUrl === 'string' &&
    Array.isArray((maybeCategory as Category).subCategories) &&
    Array.isArray((maybeCategory as Category).apps)
  );
}

export const AppsCount = composer()
  .withTranslation()
  .compose(({ count, t }: { count: number; t?: TFunction }) =>
    t('sub-category-page.apps-count', { count }),
  );
